/**
 * Provider-native option registry for the first Meta Create Ad draft slice.
 *
 * These values are deliberately separate from visual components. A field may
 * only render when its option is compatible with the selected objective and
 * account capability. The UI must never treat this file as a universal Meta
 * enum or silently emit unavailable values.
 *
 * Evidence: docs/research/competitors/marketer-com/
 * LIVE-WALK-2026-08-23-LAUNCH-ADS-FIELD-CATALOG.md
 */

export type MetaWebsiteObjective = 'sales' | 'leads' | 'traffic' | 'awareness' | 'engagement';

export type MetaObservedObjective = MetaWebsiteObjective | 'app_promotion';

export type MetaOptimizationGoal =
  | 'purchases'
  | 'conversion_value'
  | 'link_clicks'
  | 'landing_page_views'
  | 'reach'
  | 'impressions'
  | 'post_engagement'
  | 'messaging_conversations'
  | 'lead_generation'
  | 'thruplay'
  | 'app_installs';

export type MetaAttributionWindow =
  | 'one_day_click'
  | 'one_day_click_one_day_view'
  | 'seven_day_click'
  | 'seven_day_click_one_day_view';

export type MetaAudienceMode = 'advantage_plus' | 'prospecting' | 'warm' | 'existing_customers';

export type MetaBillingEvent = 'impressions' | 'link_clicks' | 'thruplay' | 'post_engagement';

export type MetaCallToAction =
  | 'shop_now'
  | 'order_now'
  | 'get_offer'
  | 'subscribe'
  | 'learn_more'
  | 'sign_up'
  | 'book_travel'
  | 'contact_us'
  | 'download'
  | 'get_quote'
  | 'watch_more'
  | 'apply_now'
  | 'book_now';

type Option<Value extends string> = Readonly<{
  value: Value;
  label: string;
  contractPath: string;
}>;

export const META_OBJECTIVE_OPTIONS = [
  {
    value: 'sales',
    label: 'Sales',
    contractPath: 'payload.meta.newAdSetDefaults.objective',
  },
  {
    value: 'leads',
    label: 'Leads',
    contractPath: 'payload.meta.newAdSetDefaults.objective',
  },
  {
    value: 'traffic',
    label: 'Traffic',
    contractPath: 'payload.meta.newAdSetDefaults.objective',
  },
  {
    value: 'awareness',
    label: 'Awareness',
    contractPath: 'payload.meta.newAdSetDefaults.objective',
  },
  {
    value: 'engagement',
    label: 'Engagement',
    contractPath: 'payload.meta.newAdSetDefaults.objective',
  },
  {
    value: 'app_promotion',
    label: 'App promotion',
    contractPath: 'payload.meta.newAdSetDefaults.objective',
    unavailableReason: 'App destinations are not available in the website-ad draft slice.',
  },
] as const satisfies readonly (Option<MetaObservedObjective> & {
  unavailableReason?: string;
})[];

const OPTIMIZATION_OPTIONS: Readonly<
  Record<MetaObservedObjective, readonly MetaOptimizationGoal[]>
> = {
  sales: [
    'purchases',
    'conversion_value',
    'link_clicks',
    'landing_page_views',
    'reach',
    'impressions',
    'post_engagement',
    'messaging_conversations',
  ],
  leads: [
    'purchases',
    'conversion_value',
    'link_clicks',
    'landing_page_views',
    'reach',
    'lead_generation',
    'impressions',
  ],
  traffic: [
    'purchases',
    'link_clicks',
    'landing_page_views',
    'reach',
    'impressions',
    'thruplay',
    'messaging_conversations',
    'post_engagement',
  ],
  awareness: ['reach', 'impressions', 'thruplay'],
  engagement: [
    'purchases',
    'thruplay',
    'reach',
    'impressions',
    'link_clicks',
    'messaging_conversations',
    'post_engagement',
    'landing_page_views',
  ],
  app_promotion: ['purchases', 'conversion_value', 'app_installs', 'link_clicks'],
};

export const META_OPTIMIZATION_LABELS: Readonly<Record<MetaOptimizationGoal, string>> = {
  purchases: 'Purchases',
  conversion_value: 'Conversion value',
  link_clicks: 'Link clicks',
  landing_page_views: 'Landing page views',
  reach: 'Reach',
  impressions: 'Impressions',
  post_engagement: 'Post engagement',
  messaging_conversations: 'Messaging conversations',
  lead_generation: 'Website leads',
  thruplay: 'ThruPlay',
  app_installs: 'App installs',
};

const DEFAULT_OPTIMIZATION: Readonly<Record<MetaObservedObjective, MetaOptimizationGoal>> = {
  sales: 'purchases',
  leads: 'lead_generation',
  traffic: 'link_clicks',
  awareness: 'impressions',
  engagement: 'post_engagement',
  app_promotion: 'app_installs',
};

const DEFAULT_ATTRIBUTION: Readonly<Record<MetaObservedObjective, MetaAttributionWindow>> = {
  sales: 'seven_day_click_one_day_view',
  leads: 'one_day_click_one_day_view',
  traffic: 'one_day_click_one_day_view',
  awareness: 'one_day_click_one_day_view',
  engagement: 'one_day_click_one_day_view',
  app_promotion: 'one_day_click_one_day_view',
};

export const META_ATTRIBUTION_OPTIONS = [
  {
    value: 'one_day_click',
    label: '1-day click',
    contractPath: 'payload.meta.adSets[].attribution',
  },
  {
    value: 'one_day_click_one_day_view',
    label: '1-day click + 1-day view',
    contractPath: 'payload.meta.adSets[].attribution',
  },
  {
    value: 'seven_day_click',
    label: '7-day click',
    contractPath: 'payload.meta.adSets[].attribution',
  },
  {
    value: 'seven_day_click_one_day_view',
    label: '7-day click + 1-day view',
    contractPath: 'payload.meta.adSets[].attribution',
  },
] as const satisfies readonly Option<MetaAttributionWindow>[];

export const META_BILLING_EVENT_OPTIONS = [
  {
    value: 'impressions',
    label: 'Impressions',
    contractPath: 'payload.meta.adSets[].billingEvent',
  },
  {
    value: 'link_clicks',
    label: 'Link clicks',
    contractPath: 'payload.meta.adSets[].billingEvent',
  },
  {
    value: 'thruplay',
    label: 'ThruPlay',
    contractPath: 'payload.meta.adSets[].billingEvent',
  },
  {
    value: 'post_engagement',
    label: 'Post engagement',
    contractPath: 'payload.meta.adSets[].billingEvent',
  },
] as const satisfies readonly Option<MetaBillingEvent>[];

export const META_AUDIENCE_MODES = [
  {
    value: 'advantage_plus',
    label: 'Advantage+ audience',
    description: 'Give Meta suggestions while keeping exclusions and strict controls explicit.',
    contractPath: 'payload.meta.adSets[].audience.mode',
  },
  {
    value: 'prospecting',
    label: 'Prospecting',
    description: 'Reach new people and make the customer-exclusion policy explicit.',
    contractPath: 'payload.meta.adSets[].audience.mode',
  },
  {
    value: 'warm',
    label: 'Warm / retargeting',
    description: 'Re-engage people from at least one included Custom Audience.',
    contractPath: 'payload.meta.adSets[].audience.mode',
  },
  {
    value: 'existing_customers',
    label: 'Existing customers',
    description: 'Cross-sell or upsell to an explicit customer-list audience.',
    contractPath: 'payload.meta.adSets[].audience.mode',
  },
] as const satisfies readonly (Option<MetaAudienceMode> & { description: string })[];

export const META_CTA_OPTIONS = [
  { value: 'shop_now', label: 'Shop now', group: 'recommended' },
  { value: 'order_now', label: 'Order now', group: 'recommended' },
  { value: 'get_offer', label: 'Get offer', group: 'recommended' },
  { value: 'subscribe', label: 'Subscribe', group: 'recommended' },
  { value: 'learn_more', label: 'Learn more', group: 'other' },
  { value: 'sign_up', label: 'Sign up', group: 'other' },
  { value: 'book_travel', label: 'Book travel', group: 'other' },
  { value: 'contact_us', label: 'Contact us', group: 'other' },
  { value: 'download', label: 'Download', group: 'other' },
  { value: 'get_quote', label: 'Get quote', group: 'other' },
  { value: 'watch_more', label: 'Watch more', group: 'other' },
  { value: 'apply_now', label: 'Apply now', group: 'other' },
  { value: 'book_now', label: 'Book now', group: 'other' },
] as const satisfies readonly {
  value: MetaCallToAction;
  label: string;
  group: 'recommended' | 'other';
}[];

export type MetaAdvantageCreativeControl =
  | 'master'
  | 'relevant_comments'
  | 'visual_touchups'
  | 'text_improvements'
  | 'add_overlays'
  | 'brightness_contrast'
  | 'music_overlay'
  | 'image_animation'
  | 'generate_backgrounds'
  | 'expand_image'
  | 'enhance_cta'
  | 'translate_text';

export const META_ADVANTAGE_CREATIVE_CONTROLS = [
  {
    value: 'master',
    label: 'Advantage+ creative',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.enabled',
    recommended: false,
  },
  {
    value: 'relevant_comments',
    label: 'Relevant comments',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.relevantComments',
    recommended: false,
  },
  {
    value: 'visual_touchups',
    label: 'Visual touch-ups',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.visualTouchups',
    recommended: true,
  },
  {
    value: 'text_improvements',
    label: 'Text improvements',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.textImprovements',
    recommended: true,
  },
  {
    value: 'add_overlays',
    label: 'Add overlays',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.addOverlays',
    recommended: false,
  },
  {
    value: 'brightness_contrast',
    label: 'Adjust brightness & contrast',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.brightnessContrast',
    recommended: true,
  },
  {
    value: 'music_overlay',
    label: 'Music overlay',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.musicOverlay',
    recommended: false,
  },
  {
    value: 'image_animation',
    label: 'Image animation',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.imageAnimation',
    recommended: false,
  },
  {
    value: 'generate_backgrounds',
    label: 'Generate backgrounds',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.generateBackgrounds',
    recommended: false,
  },
  {
    value: 'expand_image',
    label: 'Expand image',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.expandImage',
    recommended: false,
  },
  {
    value: 'enhance_cta',
    label: 'Enhance CTA',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.enhanceCta',
    recommended: false,
  },
  {
    value: 'translate_text',
    label: 'Translate text',
    contractPath: 'payload.meta.ads[].automation.advantageCreative.translateText',
    recommended: false,
  },
] as const satisfies readonly (Option<MetaAdvantageCreativeControl> & {
  recommended: boolean;
})[];

export type MetaAudienceModeInput = Readonly<{
  mode: MetaAudienceMode;
  includedCustomAudienceIds: readonly string[];
  customerListAudienceIds: readonly string[];
  customerExclusionChoice: 'inherit' | 'always_exclude' | 'never_exclude' | null;
}>;

export type MetaCompatibilityIssue = Readonly<{
  code: string;
  contractPath: string;
  message: string;
}>;

export function metaOptimizationOptions(
  objective: MetaObservedObjective,
): readonly MetaOptimizationGoal[] {
  return OPTIMIZATION_OPTIONS[objective];
}

export function metaDefaultOptimization(objective: MetaObservedObjective): MetaOptimizationGoal {
  return DEFAULT_OPTIMIZATION[objective];
}

export function metaDefaultAttribution(objective: MetaObservedObjective): MetaAttributionWindow {
  return DEFAULT_ATTRIBUTION[objective];
}

export function isMetaOptimizationCompatible(
  objective: MetaObservedObjective,
  optimization: MetaOptimizationGoal,
): boolean {
  return OPTIMIZATION_OPTIONS[objective].includes(optimization);
}

export function metaAudienceModeIssues(
  input: MetaAudienceModeInput,
): readonly MetaCompatibilityIssue[] {
  const issues: MetaCompatibilityIssue[] = [];

  if (input.mode === 'prospecting' && input.customerExclusionChoice === null) {
    issues.push({
      code: 'customer_exclusion_required',
      contractPath: 'payload.meta.adSets[].audience.customerExclusionChoice',
      message: 'Choose how this prospecting Ad Set handles existing customers.',
    });
  }

  if (input.mode === 'warm' && input.includedCustomAudienceIds.length === 0) {
    issues.push({
      code: 'warm_audience_required',
      contractPath: 'payload.meta.adSets[].audience.includedCustomAudienceIds',
      message: 'Choose at least one Custom Audience for warm retargeting.',
    });
  }

  if (input.mode === 'existing_customers') {
    if (input.customerListAudienceIds.length === 0) {
      issues.push({
        code: 'customer_list_required',
        contractPath: 'payload.meta.adSets[].audience.customerListAudienceIds',
        message: 'Choose at least one customer-list audience.',
      });
    }
    if (input.customerExclusionChoice === 'always_exclude') {
      issues.push({
        code: 'customer_exclusion_incompatible',
        contractPath: 'payload.meta.adSets[].audience.customerExclusionChoice',
        message: 'Existing-customer Ad Sets cannot exclude the customer audience they target.',
      });
    }
  }

  return issues;
}

export type MetaDynamicCreativeAction = 'add_ad' | 'duplicate_ad' | 'bulk_select';

export function metaDynamicCreativeActionIssue(
  dynamicCreative: boolean,
  action: MetaDynamicCreativeAction,
): MetaCompatibilityIssue | null {
  if (!dynamicCreative) return null;
  return {
    code: 'dynamic_creative_single_ad',
    contractPath: 'payload.meta.adSets[].dynamicCreative',
    message: `${action === 'add_ad' ? 'Adding' : action === 'duplicate_ad' ? 'Duplicating' : 'Bulk editing'} Ads is unavailable because Dynamic Creative allows one Ad in this Ad Set.`,
  };
}

export function metaRecommendedAdvantageCreativeValues(): Readonly<
  Partial<Record<Exclude<MetaAdvantageCreativeControl, 'master'>, boolean>>
> {
  return Object.fromEntries(
    META_ADVANTAGE_CREATIVE_CONTROLS.filter((control) => control.value !== 'master').map(
      (control) => [control.value, control.recommended],
    ),
  );
}
